import { Form, Space } from 'antd';
import dayjs from 'dayjs';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  useDeleteRecordsMutation,
  useLazyGetRecordFilterListsQuery,
  useLazyGetRecordsSearchResultQuery,
  useSearchRecordsMutation,
  useSetRecordsTagsMutation,
} from '@app/api/Records/api';
import { useLazyGetTagsQuery } from '@app/api/Tags';
import { MassActionsDropdown } from '@app/components';
import { openRecordByIdInModal } from '@app/components/RecordModal/model/recordModalSlice';
import {
  SUCCESS,
  COMPLETED,
  CONFIRMED_LABEL,
  EXPIRED,
  IN_PROGRESS,
  NOVISIT_LABEL,
  VISITED_LABEL,
  WAIT,
  WAITING_LABEL,
} from '@app/constants';
import { ClientStatues } from '@app/constants/recordStatuses';
import { TOOLBAR_SPACE } from '@app/constants/toolbarClasses';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { HeaderContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { WidthArchiveLabel } from '@app/shared/components';
import type {
  MultiSelectValue,
  MultiSelectOption,
} from '@app/shared/components/MultiSelect/types';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { useAppDispatch } from '@app/store';
import type { NRecords } from '@app/types';
import type { NTables } from '@app/types/Tables';
import type { NTags } from '@app/types/Tags';

import { massActionMenu } from '@pages/Clients/Records/data/massActionMenu';
import type { NRecordsList } from '@pages/Clients/Records/RecordsList.types';
import { msToInt32ItemsFilters } from '@pages/Clients/Records/utils/msToInt32ItemsFilters';
import {
  clearStoredRecordsFilters,
  pickOptionsByValues,
  readStoredRecordsFilters,
  saveStoredRecordsFilters,
  toStoredDateRange,
} from '@pages/Clients/Records/utils/recordsFiltersStorage';

import type { BaseOptionType } from 'antd/es/select';
import type { TableRowSelection } from 'antd/es/table/interface';
import type { Dayjs } from 'dayjs';

const { useForm, useWatch } = Form;
type Options = BaseOptionType[] | null;
type Type = 'checkbox' | 'radio';

export interface FormValues {
  Search: string;
  DateCreate: [Dayjs | null, Dayjs | null] | null;
  DateVisit: [Dayjs | null, Dayjs | null] | null;
  selectedEmployees: Array<number>;
  selectedUsers: Array<number>;
  selectedStatuses: Array<string>;
  selectedCategories: Array<number>;
  selectedServices: Array<number>;
  selectedRecordTags: MultiSelectValue;
  selectedClientTags: MultiSelectValue;
}

const TODAY_DATE = dayjs();

const initialSearchRecordsRequest: NRecords.ModelRecordSearchFilter = {
  SearchTerm: null,
  DateCreateS: TODAY_DATE,
  DateCreateE: TODAY_DATE,
  DateVisitS: null,
  DateVisitE: null,
  EmployeeIds: null,
  UserIds: null,
  ServiceCategoryIds: null,
  ServiceIds: null,
  Statuses: null,
  RecordsPerPage: 20,
  AdditionalSaleIds: null,
  PaymentStatus: 'All',
  RecordTagsFilters: null,
  ClientTagsFilters: null,
  Types: null,
};

export const useRecords = ({
  rowsPerPage,
  searchId,
  records,
}: NRecordsList.UseRecordsProps) => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [deletionLoader, setDeletionLoader] = useState(false);
  const [isArchivingModalOpen, setIsArchivingModalOpen] = useState(false);
  const [tagsLoader, setTagsLoader] = useState(false);
  const [tagsModal, setTagsModal] = useState(false);

  // Снимок применённых фильтров прошлого визита читается один раз при
  // монтировании: из него собирается стартовый поиск и восстанавливаются
  // контролы, когда подъедут списки фильтров (issue/11073).
  const storedFiltersRef = useRef(readStoredRecordsFilters());
  const areSelectionsRestoredRef = useRef(false);

  const [filters, setFilters] =
    useState<NRecords.ModelRecordFilterLists | null>(null);

  const [selectedEmployees, setSelectedEmployees] = useState<Options>(null);
  const [selectedUsers, setSelectedUsers] = useState<Options>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<Options>(null);
  const [selectedCategories, setSelectedCategories] = useState<Options>(null);
  const [selectedServices, setSelectedServices] = useState<Options>(null);
  const [selectedRecordTypes, setSelectedRecordTypes] = useState<Options>(null);
  const [selectedAdditionalSales, setSelectedAdditionalSales] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<
    NRecords.ModelRecordSearchFilter['PaymentStatus']
  >(storedFiltersRef.current?.request.PaymentStatus ?? 'All');
  const [searchError, setSearchError] = useState('');
  const [selectionType] = useState<Type>('checkbox');
  const toolbarClass = TOOLBAR_SPACE;

  const [tags, setTags] = useState<NTags.Tag[]>([]);
  const [selectedTagsIds, setSelectedTagsIds] = useState<string[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const { innerWidth } = useWindowSize();
  const { userId, database, activeBranchId } = useUserPrint();
  const [getTags] = useLazyGetTagsQuery();
  const [searchRecords] = useSearchRecordsMutation();
  const [getRecordFilterLists] = useLazyGetRecordFilterListsQuery();
  const [getRecordsSearchResult] = useLazyGetRecordsSearchResultQuery();
  const [deleteRecords] = useDeleteRecordsMutation();
  const [setRecordsTags] = useSetRecordsTagsMutation();

  const isMobile = useMemo(() => innerWidth <= 550, [innerWidth]);

  const [form] = useForm<FormValues>();

  const formValues = useWatch([], form);

  const { showNotification } = useNotification();
  const makeMsInit = (): MultiSelectValue => ({
    groups: [{ id: 'g1', logic: 'AND', selectedValues: [] }],
  });

  const [recordTagsMS, setRecordTagsMS] = useState<MultiSelectValue>(
    storedFiltersRef.current?.recordTags ?? makeMsInit()
  );
  const [clientTagsMS, setClientTagsMS] = useState<MultiSelectValue>(
    storedFiltersRef.current?.clientTags ?? makeMsInit()
  );

  const recordTagsMsOptions: MultiSelectOption[] = useMemo(() => {
    if (!filters?.Tags?.length) return [];
    return filters.Tags.map(({ Id, Name }) => ({
      label: Name ?? '',
      value: String(Id),
    }));
  }, [filters]);

  const clientTagsMsOptions: MultiSelectOption[] = useMemo(() => {
    if (!filters?.ClientTags?.length) return [];
    return filters.ClientTags.map(({ Id, Name }) => ({
      label: Name ?? '',
      value: String(Id),
    }));
  }, [filters]);

  const {
    setHeader: setRecordsProviderData,
    setIsLoading,
    header: recordsProviderData,
  } = useContext(HeaderContext);

  useEffect(() => {
    setRecordsProviderData((prev) => ({
      ...prev,
      formValues,
      recordTagsMS,
      clientTagsMS,
    }));
  }, [
    formValues,
    setRecordsProviderData,
    selectedStatuses,
    recordTagsMS,
    clientTagsMS,
  ]);

  const {
    userAccessRights: {
      Records: { IsEdit, IsDelete, IsViewPastRecords, IsRegister },
    },
  } = useNewUserAccessRights();

  const restrictPastSearch = !IsViewPastRecords;

  const disabledPastDate = useMemo(() => {
    const today = dayjs().startOf('day');
    return (current?: Dayjs) =>
      !!current && restrictPastSearch && current.isBefore(today, 'day');
  }, [restrictPastSearch]);

  const clampRangeToTodayIfRestricted = useCallback(
    (
      range: [Dayjs | null, Dayjs | null] | null
    ): [Dayjs | null, Dayjs | null] | null => {
      if (!restrictPastSearch || !range) return range;

      const today = dayjs().startOf('day');

      const s0 = range?.[0];
      const e0 = range?.[1];

      const s = s0 && s0.isBefore(today, 'day') ? today : s0;
      let e = e0 && e0.isBefore(today, 'day') ? today : e0;

      if (s && e && e.isBefore(s)) e = s;
      return [s ?? null, e ?? null];
    },
    [restrictPastSearch]
  );
  const dispatch = useAppDispatch();

  const selectionIds = useMemo(() => {
    const ids = recordsProviderData?.selectionIds;
    return Array.isArray(ids) && ids.every((id) => typeof id === 'string')
      ? ids
      : [];
  }, [recordsProviderData]);

  const getMassActionDisabled = useCallback(
    (key: string) => {
      if (key === '1') {
        return selectionIds.length === 0 || !IsEdit;
      } else if (key === '2') {
        return selectionIds.length === 0 || !IsDelete;
      }
      return false;
    },
    [selectionIds, IsEdit, IsDelete]
  );

  const getMassActionCallback = useCallback(
    (key: string) => () => {
      if (getMassActionDisabled(key)) return;

      switch (key) {
        case '1':
          setTagsModal(true);
          break;
        case '2':
          setIsArchivingModalOpen(true);
          break;
        default:
          break;
      }
    },
    [getMassActionDisabled]
  );

  const massActionItems = useMemo(
    () =>
      massActionMenu.map(({ key, title, Icon, dataTestId }) => ({
        key,
        label: (
          <Space onClick={getMassActionCallback(key)} data-testid={dataTestId}>
            <Icon />
            {title}
          </Space>
        ),
        disabled: getMassActionDisabled(key),
      })),
    [getMassActionDisabled, getMassActionCallback]
  );

  const MassActionButton = useMemo(
    () => (
      <div className="mass-actions">
        <MassActionsDropdown
          items={massActionItems}
          data={records}
          selectionCount={selectionIds.length || 0}
          buttonDataTestId="actions-button"
        />
      </div>
    ),
    [massActionItems, records, selectionIds.length]
  );

  const formatDate = useCallback(
    (date: Dayjs | null) => (date ? date.format('YYYY-MM-DD') : null),
    []
  );

  const getIds = useCallback(
    (items: Options) =>
      items && items?.length > 0 ? items?.map(({ value }) => value) : null,
    []
  );

  // Сохранённый фильтр подчиняется тем же правилам, что и обычный поиск: без
  // права на прошлые записи период подтягивается к сегодняшнему дню, иначе
  // после возврата пользователь увидел бы прошлые записи (issue/11073).
  const restoredSearchRequest = useMemo(() => {
    const storedFilters = storedFiltersRef.current;

    if (!storedFilters) return null;

    const { request } = storedFilters;

    const clampedCreate = clampRangeToTodayIfRestricted(
      toStoredDateRange(request.DateCreateS, request.DateCreateE)
    );
    const clampedVisit = clampRangeToTodayIfRestricted(
      toStoredDateRange(request.DateVisitS, request.DateVisitE)
    );

    return {
      ...request,
      DateCreateS: clampedCreate ? formatDate(clampedCreate[0]) : null,
      DateCreateE: clampedCreate ? formatDate(clampedCreate[1]) : null,
      DateVisitS: clampedVisit ? formatDate(clampedVisit[0]) : null,
      DateVisitE: clampedVisit ? formatDate(clampedVisit[1]) : null,
    };
  }, [clampRangeToTodayIfRestricted, formatDate]);

  const searchRecordHandler = useCallback(
    async (data: NRecords.ModelRecordSearchFilter) => {
      setIsLoadingData(true);

      try {
        const { Records, Id, CountRecords } = await searchRecords({
          ActiveBranchId: activeBranchId,
          data,
          logs: { isLogged: true, userId, database },
        }).unwrap();

        setRecordsProviderData((prev) => ({
          ...prev,
          records: Records,
          searchId: Id,
          totalRowsCount: CountRecords,
        }));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      } finally {
        setIsLoadingData(false);
      }
    },
    [
      activeBranchId,
      database,
      userId,
      setRecordsProviderData,
      setIsLoadingData,
      searchRecords,
    ]
  );

  const getInitialTableData = useCallback(async () => {
    try {
      // Фильтры прошлого визита применяются сразу: пользователь возвращается
      // из карточки к той же выборке, а не к фильтрам по умолчанию.
      await searchRecordHandler(
        restoredSearchRequest
          ? {
              ...restoredSearchRequest,
              RecordsPerPage: initialSearchRecordsRequest.RecordsPerPage,
            }
          : initialSearchRecordsRequest
      );
      setRecordsProviderData((prev) => ({
        ...prev,
        rowsPerPage: 20,
        pageNumber: 1,
      }));

      const filterLists = await getRecordFilterLists({
        ActiveBranchId: activeBranchId,
        logs: { isLogged: true, userId, database },
      }).unwrap();
      setFilters(filterLists);

      setIsLoading(false);
      const tagsResult = await getTags({
        ActiveBranchId: activeBranchId,
      }).unwrap();
      setTags(
        tagsResult.map((tag) => ({
          ...tag,
          Name: tag.Name ?? '',
          Color: tag.Color ?? '',
          IconId: tag.IconId ?? '',
        }))
      );
    } catch (error) {
      showNotification(error);
    } finally {
      setIsLoadingData(false);
    }
  }, [
    activeBranchId,
    database,
    userId,
    getTags,
    getRecordFilterLists,
    restoredSearchRequest,
    searchRecordHandler,
    setRecordsProviderData,
    showNotification,
    setIsLoading,
  ]);

  const submitFormHandler = useCallback(
    async ({ Search, DateVisit, DateCreate }: FormValues) => {
      if (Search && Search.length < 3) return;

      const clampedVisit = clampRangeToTodayIfRestricted(DateVisit);
      const clampedCreate = clampRangeToTodayIfRestricted(DateCreate);

      const DateVisitS = clampedVisit ? formatDate(clampedVisit[0]) : null;
      const DateVisitE = clampedVisit ? formatDate(clampedVisit[1]) : null;
      const DateCreateS = clampedCreate ? formatDate(clampedCreate[0]) : null;
      const DateCreateE = clampedCreate ? formatDate(clampedCreate[1]) : null;

      const EmployeeIds = getIds(selectedEmployees);
      const UserIds = getIds(selectedUsers);
      const ServiceCategoryIds = getIds(selectedCategories);
      const ServiceIds = getIds(selectedServices);
      const Statuses = getIds(selectedStatuses);
      const RecordsPerPage = rowsPerPage;
      const AdditionalSaleIds = getIds(selectedAdditionalSales);
      const SearchTerm = Search ? Search : '';
      const Types = getIds(selectedRecordTypes);

      const RecordTagsFilters = msToInt32ItemsFilters(recordTagsMS);
      const ClientTagsFilters = msToInt32ItemsFilters(clientTagsMS);

      try {
        await searchRecordHandler({
          SearchTerm,
          DateCreateS,
          DateCreateE,
          DateVisitS,
          DateVisitE,
          EmployeeIds,
          UserIds,
          ServiceCategoryIds,
          ServiceIds,
          Statuses,
          RecordsPerPage,
          AdditionalSaleIds,
          RecordTagsFilters,
          ClientTagsFilters,
          PaymentStatus: selectedPaymentStatus,
          Types,
        });

        // Сохраняем только применённый фильтр — тот, по которому поиск
        // действительно выполнился (issue/11073).
        saveStoredRecordsFilters({
          request: {
            SearchTerm,
            DateCreateS,
            DateCreateE,
            DateVisitS,
            DateVisitE,
            EmployeeIds,
            UserIds,
            ServiceCategoryIds,
            ServiceIds,
            Statuses,
            AdditionalSaleIds,
            RecordTagsFilters,
            ClientTagsFilters,
            PaymentStatus: selectedPaymentStatus,
            Types,
          },
          recordTags: recordTagsMS,
          clientTags: clientTagsMS,
        });
      } catch (error) {
        const message = handleCatchError(error);
        setSearchError(message);
      } finally {
        setIsLoadingData(false);
      }
    },
    [
      selectedEmployees,
      selectedUsers,
      selectedCategories,
      selectedServices,
      selectedStatuses,
      rowsPerPage,
      selectedAdditionalSales,
      recordTagsMS,
      clientTagsMS,
      searchRecordHandler,
      formatDate,
      getIds,
      selectedPaymentStatus,
      clampRangeToTodayIfRestricted,
      selectedRecordTypes,
    ]
  );

  const submitForm = useCallback(() => {
    form.submit();
  }, [form]);

  const handlePageChange = useCallback(
    async (page: number, pageSize: number) => {
      document.querySelector('.page-content')?.scrollTo(0, 0);

      if (searchId) {
        setIsLoadingData(true);

        try {
          const { Records, PageN } = await getRecordsSearchResult({
            ActiveBranchId: activeBranchId,
            searchId,
            page: pageSize === rowsPerPage ? page : 1,
            rowsPerPage: pageSize,
          }).unwrap();

          setRecordsProviderData({
            ...recordsProviderData,
            records: Records,
            rowsPerPage: pageSize,
            pageNumber: PageN,
          });
        } catch (error) {
          showNotification(error);
        } finally {
          setIsLoadingData(false);
        }
      }
    },
    [
      activeBranchId,
      rowsPerPage,
      searchId,
      recordsProviderData,
      showNotification,
      setRecordsProviderData,
      getRecordsSearchResult,
    ]
  );

  const handleReset = useCallback(async () => {
    setIsLoadingData(true);

    storedFiltersRef.current = null;
    clearStoredRecordsFilters();

    form.resetFields();
    setSelectedAdditionalSales([]);
    setSelectedEmployees([]);
    setSelectedUsers([]);
    setSelectedServices([]);
    setSelectedStatuses([]);
    setSelectedPaymentStatus('All');
    setSelectedCategories(null);
    setSelectedRecordTypes(null);

    setRecordTagsMS(makeMsInit());
    setClientTagsMS(makeMsInit());

    try {
      await searchRecordHandler({
        ...initialSearchRecordsRequest,
        RecordsPerPage: rowsPerPage,
        RecordTagsFilters: null,
        ClientTagsFilters: null,
      });
      setRecordsProviderData((prev) => ({
        ...prev,
        rowsPerPage: 20,
        pageNumber: 1,
        searchId: null,
      }));
    } catch (error) {
      showNotification(error);
    } finally {
      setIsLoadingData(false);
    }
  }, [
    rowsPerPage,
    searchRecordHandler,
    setRecordsProviderData,
    showNotification,
    form,
  ]);

  const getStatus = (status: string | null) => {
    switch (status) {
      case WAITING_LABEL:
        return WAIT;
      case CONFIRMED_LABEL:
        return IN_PROGRESS;
      case VISITED_LABEL:
        return COMPLETED;
      case NOVISIT_LABEL:
        return EXPIRED;
      default:
        return '';
    }
  };

  const rowSelection: TableRowSelection<NTables.RecordsServiceHeader> = useMemo(
    () => ({
      renderCell(checked, record, index, node) {
        return (
          <div data-testid="operation-checkbox" className="checkbox-container">
            {node}
          </div>
        );
      },
      selectedRowKeys,
      onChange: (
        selectedRowKeys: React.Key[],
        selectedRows: NTables.RecordsServiceHeader[]
      ) => {
        setRecordsProviderData((prev) => ({
          ...prev,
          selectionIds: selectedRowKeys,
          selectionCount: selectedRows.length,
        }));
        setSelectedRowKeys(selectedRowKeys);
      },
      columnTitle: (checkboxNode: React.ReactNode) =>
        generateSpanWithDataTestId(checkboxNode, 'all-operations-checkbox'),
    }),
    [selectedRowKeys, setRecordsProviderData]
  );

  const deleteRows = useCallback(async () => {
    try {
      const selectionIdsAsNumber = selectionIds.map((item: string) =>
        Number(item)
      );
      setIsArchivingModalOpen(false);
      setDeletionLoader(true);
      await deleteRecords({
        ActiveBranchId: activeBranchId,
        data: selectionIdsAsNumber,
      }).unwrap();
      submitForm();
    } catch (error) {
      showNotification(error);
    } finally {
      setDeletionLoader(false);
    }
  }, [
    activeBranchId,
    selectionIds,
    showNotification,
    submitForm,
    deleteRecords,
  ]);

  const updateTags = useCallback(async () => {
    try {
      const selectionIdsAsNumber = selectionIds.map((item: string) =>
        Number(item)
      );
      setTagsLoader(true);
      await setRecordsTags({
        ActiveBranchId: activeBranchId,
        data: {
          RecordIds: selectionIdsAsNumber,
          TagIds: selectedTagsIds.map((id) => +id),
        },
      }).unwrap();

      const filterLists = await getRecordFilterLists({
        ActiveBranchId: activeBranchId,
        logs: { isLogged: true, userId, database },
      }).unwrap();
      setFilters(filterLists);
      setSelectedRowKeys([]);
      setRecordsProviderData((prev) => ({
        ...prev,
        selectionIds: [],
      }));
      showNotification('Теги успешно добавлены', SUCCESS);
    } catch (error) {
      showNotification(error);
    } finally {
      setTagsModal(false);
      setTagsLoader(false);
      setSelectedTagsIds([]);
    }
  }, [
    selectionIds,
    userId,
    database,
    activeBranchId,
    selectedTagsIds,
    showNotification,
    setRecordsProviderData,
    setRecordsTags,
    getRecordFilterLists,
  ]);

  const onOpenRecord = useCallback(
    (id: number) => {
      dispatch(openRecordByIdInModal(id));
    },
    [dispatch]
  );

  const onOpenRecordNewTab = useCallback(
    (id: number) => {
      const currentParams = new URLSearchParams(searchParams);
      currentParams.set('recordId', id.toString());

      const newSearch = currentParams.toString();
      const url = `${window.location.origin}${pathname}?${newSearch}`;

      const newTab = window.open(url, '_blank');
      if (newTab) {
        newTab?.focus();
      }
    },
    [pathname, searchParams]
  );

  const getSelectOptions = useCallback(
    (data?: NRecords.ModelItem[] | null, deletedDataTestId: string = '') => {
      if (!data?.length) return [];

      return [...data]
        .sort((a, b) => {
          const isADeleted = a.IsDeleted || false;
          const isBDeleted = b.IsDeleted || false;

          if (isADeleted !== isBDeleted) {
            return isADeleted ? 1 : -1;
          }
          return a?.Name?.localeCompare(b?.Name ?? '') || 0;
        })
        .map(({ Id, Name, IsDeleted }) => ({
          label: (
            <WidthArchiveLabel
              title={Name}
              isDeleted={IsDeleted}
              data-testid={deletedDataTestId}
            />
          ),
          value: Id,
          filterName: Name,
        }));
    },
    []
  );

  const employeesSelectOptions = useMemo(
    () => getSelectOptions(filters?.Employees, 'archive-executor'),
    [filters, getSelectOptions]
  );

  const usersSelectOptions = useMemo(
    () => getSelectOptions(filters?.Users),
    [filters, getSelectOptions]
  );

  const serviceCategoriesSelectOptions = useMemo(
    () => getSelectOptions(filters?.ServiceCategories, 'archive-category'),
    [filters, getSelectOptions]
  );

  const servicesSelectOptions = useMemo(
    () => getSelectOptions(filters?.Services, 'archive-service'),
    [filters, getSelectOptions]
  );

  const clientStatusSelectOptions = useMemo(
    () =>
      ClientStatues?.map(({ status, label }) => ({
        label,
        value: status,
      })) ?? [],
    []
  );

  const additionalSalesSelectOptions = useMemo(() => {
    if (!filters?.AdditionalSales?.length) return [];

    const { AdditionalSales } = filters;

    return AdditionalSales.map(({ Id, Name }) => ({
      label: Name,
      value: Id,
    }));
  }, [filters]);

  const recordTypesSelectOptions = useMemo(() => {
    if (!filters?.RecordTypes?.length) return [];

    const { RecordTypes } = filters;

    return RecordTypes.map(({ IdS, Name }) => ({
      label: Name,
      value: IdS,
    }));
  }, [filters]);

  // Значения формы возвращаются сразу, а выбранные опции мультиселектов —
  // когда придут списки фильтров (GetRecordFilterLists): именно из них эти
  // опции и собираются, по идентификаторам из сохранённого фильтра.
  useEffect(() => {
    if (!restoredSearchRequest) return;

    form.setFieldsValue({
      Search: restoredSearchRequest.SearchTerm ?? '',
      DateCreate:
        toStoredDateRange(
          restoredSearchRequest.DateCreateS,
          restoredSearchRequest.DateCreateE
        ) ?? undefined,
      DateVisit:
        toStoredDateRange(
          restoredSearchRequest.DateVisitS,
          restoredSearchRequest.DateVisitE
        ) ?? undefined,
    });
  }, [form, restoredSearchRequest]);

  useEffect(() => {
    const request = restoredSearchRequest;

    if (!request || !filters || areSelectionsRestoredRef.current) return;

    areSelectionsRestoredRef.current = true;

    setSelectedEmployees(
      pickOptionsByValues(employeesSelectOptions, request.EmployeeIds)
    );
    setSelectedUsers(pickOptionsByValues(usersSelectOptions, request.UserIds));
    setSelectedStatuses(
      pickOptionsByValues(clientStatusSelectOptions, request.Statuses)
    );
    setSelectedCategories(
      pickOptionsByValues(
        serviceCategoriesSelectOptions,
        request.ServiceCategoryIds
      )
    );
    setSelectedServices(
      pickOptionsByValues(servicesSelectOptions, request.ServiceIds)
    );
    setSelectedAdditionalSales(
      pickOptionsByValues(
        additionalSalesSelectOptions,
        request.AdditionalSaleIds
      )
    );
    setSelectedRecordTypes(
      pickOptionsByValues(recordTypesSelectOptions, request.Types)
    );
  }, [
    additionalSalesSelectOptions,
    clientStatusSelectOptions,
    employeesSelectOptions,
    filters,
    recordTypesSelectOptions,
    restoredSearchRequest,
    serviceCategoriesSelectOptions,
    servicesSelectOptions,
    usersSelectOptions,
  ]);

  return {
    selectionCount: selectionIds.length || 0,
    isLoadingData,
    handlePageChange,
    form,
    submitFormHandler,
    selectedEmployees,
    setSelectedEmployees,
    selectedUsers,
    setSelectedUsers,
    selectedStatuses,
    setSelectedStatuses,
    selectedCategories,
    setSelectedCategories,
    searchError,
    setSearchError,
    selectionType,
    selectedServices,
    setSelectedServices,
    handleReset,
    rowSelection,
    deletionLoader,
    isArchivingModalOpen,
    setIsArchivingModalOpen,
    deleteRows,
    tagsLoader,
    tagsModal,
    setTagsModal,
    updateTags,
    setSelectedTagsIds,
    selectedTagsIds,
    tags,
    toolbarClass,
    isMobile,
    MassActionButton,
    IsRegister,
    submitForm,
    getInitialTableData,
    selectedAdditionalSales,
    setSelectedAdditionalSales,
    employeesSelectOptions,
    usersSelectOptions,
    serviceCategoriesSelectOptions,
    servicesSelectOptions,
    onOpenRecord,
    onOpenRecordNewTab,
    clientStatusSelectOptions,
    additionalSalesSelectOptions,
    recordTagsMS,
    setRecordTagsMS,
    clientTagsMS,
    setClientTagsMS,
    recordTagsMsOptions,
    clientTagsMsOptions,
    setSelectedPaymentStatus,
    selectedPaymentStatus,
    disabledPastDate,
    restrictPastSearch,
    recordTypesSelectOptions,
    setSelectedRecordTypes,
    selectedRecordTypes,
    getStatus,
  };
};
